import { Fragment } from "react";
import { motion } from "framer-motion";
import { Check, Minus } from "lucide-react";
import { useTranslation } from "react-i18next";
import { PRICING_COMPARE, PRICING_TIERS } from "@/lib/pricingCompare";
import { SectionHeading } from "@/components/site/SectionHeading";

/**
 * PricingComparisonTable
 * Responsive feature-comparison table across Launch / Growth / Scale / Dominance.
 * - Sticky first column on horizontal scroll (mobile & narrow viewports)
 * - Highlighted "Scale" column matching the pricing card treatment
 * - All row labels & group headings are i18n-driven (fallback to English)
 */
export function PricingComparisonTable() {
  const { t } = useTranslation();

  const renderCell = (value, highlighted) => {
    if (value === true) {
      return (
        <span
          className={`inline-flex h-6 w-6 items-center justify-center rounded-full ${
            highlighted ? "bg-blue-600 text-white" : "bg-emerald-100 text-emerald-700"
          }`}
          aria-label={t("common.included")}
        >
          <Check className="h-3.5 w-3.5" strokeWidth={3} />
        </span>
      );
    }
    if (value === false) {
      return (
        <span
          className="inline-flex h-6 w-6 items-center justify-center rounded-full bg-slate-100 text-slate-400"
          aria-label={t("common.notIncluded")}
        >
          <Minus className="h-3.5 w-3.5" />
        </span>
      );
    }
    return (
      <span className={`text-sm font-medium ${highlighted ? "text-slate-900" : "text-slate-700"}`}>
        {value}
      </span>
    );
  };

  return (
    <section
      className="mx-auto w-full max-w-7xl px-4 pb-12 sm:px-6 sm:pb-16 lg:px-8"
      data-testid="pricing-comparison-section"
    >
      <SectionHeading
        eyebrow={t("sections.pricingCompare.eyebrow")}
        title={t("sections.pricingCompare.title")}
        description={t("sections.pricingCompare.description")}
        align="center"
        testIdPrefix="pricing-compare"
      />

      <motion.div
        initial={{ opacity: 0, y: 16 }}
        whileInView={{ opacity: 1, y: 0 }}
        viewport={{ once: true, margin: "-40px" }}
        transition={{ duration: 0.4 }}
        className="mt-10 overflow-hidden rounded-2xl border border-slate-200 bg-white shadow-[0_4px_16px_rgba(15,23,42,0.04)]"
      >
        <div className="overflow-x-auto">
          <table
            className="w-full min-w-[720px] border-collapse text-left"
            data-testid="pricing-compare-table"
          >
            <caption className="sr-only">
              {t("sections.pricingCompare.caption")}
            </caption>

            {/* Header row: tier names */}
            <thead>
              <tr className="border-b border-slate-200 bg-slate-50/70">
                <th
                  scope="col"
                  className="sticky left-0 z-10 w-[240px] bg-slate-50/70 px-5 py-4 text-xs font-bold uppercase tracking-[0.18em] text-slate-500 sm:w-[280px]"
                >
                  {t("sections.pricingCompare.featureColumn")}
                </th>
                {PRICING_TIERS.map((tier) => (
                  <th
                    key={tier.key}
                    scope="col"
                    className={`px-4 py-4 text-center align-middle ${
                      tier.highlighted ? "bg-blue-50/60" : ""
                    }`}
                    data-testid={`pricing-compare-col-${tier.key}`}
                  >
                    <div className="flex flex-col items-center gap-1">
                      {tier.highlighted && (
                        <span className="inline-flex items-center rounded-full bg-blue-600 px-2 py-0.5 text-[10px] font-bold uppercase tracking-wider text-white">
                          {t("common.mostPopular")}
                        </span>
                      )}
                      <span className="text-sm font-extrabold uppercase tracking-wider text-slate-900">
                        {t(`content.${tier.tk}.name`)}
                      </span>
                    </div>
                  </th>
                ))}
              </tr>
            </thead>

            <tbody>
              {PRICING_COMPARE.map((group) => (
                <Fragment key={`group-${group.groupKey}`}>
                  {/* Group heading row */}
                  <tr className="border-b border-slate-200 bg-slate-50/40">
                    <th
                      scope="row"
                      colSpan={PRICING_TIERS.length + 1}
                      className="sticky left-0 z-10 bg-slate-50/40 px-5 py-3 text-xs font-bold uppercase tracking-[0.18em] text-blue-700"
                      data-testid={`pricing-compare-group-${group.groupKey}`}
                    >
                      {t(`content.pricingCompare.groups.${group.groupKey}`)}
                    </th>
                  </tr>

                  {group.rows.map((row) => (
                    <tr
                      key={`${group.groupKey}-${row.key}`}
                      className="border-b border-slate-100 last:border-b-0 hover:bg-slate-50/60"
                      data-testid={`pricing-compare-row-${row.key}`}
                    >
                      <th
                        scope="row"
                        className="sticky left-0 z-10 bg-white px-5 py-3.5 text-sm font-medium text-slate-800"
                      >
                        {t(`content.pricingCompare.rows.${row.key}`)}
                      </th>
                      {PRICING_TIERS.map((tier) => (
                        <td
                          key={tier.key}
                          className={`px-4 py-3.5 text-center align-middle ${
                            tier.highlighted ? "bg-blue-50/40" : ""
                          }`}
                          data-testid={`pricing-compare-cell-${row.key}-${tier.key}`}
                        >
                          {renderCell(row[tier.key], tier.highlighted)}
                        </td>
                      ))}
                    </tr>
                  ))}
                </Fragment>
              ))}
            </tbody>
          </table>
        </div>
      </motion.div>

      <p
        className="mt-4 text-center text-xs text-slate-500"
        data-testid="pricing-compare-footnote"
      >
        {t("sections.pricingCompare.footnote")}
      </p>
    </section>
  );
}
